import React from 'react'

import {
  Accordion,
  AlignItems,
  Body2,
  Bold,
  BorderStyle,
  Box,
  Color,
  Display,
  FontFamily,
  Heading2,
  Heading3,
  Icon,
  IconVariant,
  JustifyContent,
  Link,
  LinkVariant,
  Space,
  Text,
} from '@gousto-internal/citrus-react'
import { LandingPageButton } from '../../../../components'
import { HowGoustoFoodBoxesWorkTypes } from '../../../../types'
import {
  MealPlanningSections,
  simpleTrackRedirect,
  TargetUri,
  TrackingActions,
} from '../../../../types/tracking'

const renderAccordionHeader = (question: string) => {
  const AccordionHeader = (isExpanded: boolean) => (
    <Box
      display={Display.Flex}
      justifyContent={JustifyContent.SpaceBetween}
      alignItems={AlignItems.Center}
    >
      <Heading3 color={Color.White} fontFamily={FontFamily.Bold} size={6}>
        {question}
      </Heading3>

      <Text color={Color.White} size={15}>
        <Icon
          variant={IconVariant.Inherit}
          name={isExpanded ? 'chevron_large_up' : 'chevron_large_down'}
        />
      </Text>
    </Box>
  )

  return AccordionHeader
}

export const FAQSection = ({ startOnboarding }: HowGoustoFoodBoxesWorkTypes) => (
  <Box paddingH={[8, 20]} paddingV={16} display={Display.Block} bg={Color.ColdGrey_600}>
    <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
      <Box maxWidth="1033px" width="100%">
        <Box paddingBottom={2}>
          <Heading2 color={Color.White}>Gousto menu FAQs</Heading2>
        </Box>
        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion
            id="0"
            title={renderAccordionHeader('Which is cheaper? Gousto or HelloFresh?')}
          >
            <Box paddingBottom={2}>
              <Body2 color={Color.White} size={4}>
                Gousto meals start from just £3.09 per portion. A Gousto box costs between £18.99 to
                £61.49, depending on the portion amounts, number of recipes selected and whether you
                have a subscription. As an example, if you ordered a box for 2 people, with 5
                recipes inside, you&apos;d expect to pay £48.74 (including delivery).
                <Space size={4} />
                Please find more information on our{' '}
                <LandingPageButton
                  startOnboarding={startOnboarding}
                  asLink
                  sectionName={MealPlanningSections.FAQ_SECTION}
                  target={TargetUri.BOX_PRICES}
                  action={TrackingActions.BOX_PRICING_NAVIGATION_CLICKED}
                  whiteText
                >
                  <Bold>box prices page.</Bold>
                </LandingPageButton>{' '}
                <Space size={4} />
                HelloFresh meals start from just £3.25 per portion. The cost of your HelloFresh
                recipe box differs depending on how many people you&apos;re catering for and how
                many days per week. If you purchased a box for 2 people, with 5 recipes inside,
                you&apos;d expect to pay £49.48 (including shipping). 
              </Body2>
            </Box>
          </Accordion>
        </Box>
        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion
            id="1"
            title={renderAccordionHeader('How much is a 2 person Gousto food box?')}
          >
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                A box for 2 people, with 5 recipes inside, costs £44.75 (excluding delivery). You
                can order fewer recipes for the week, and this will decrease the cost of your box.
              </Body2>
              <Space size={4} />
              <Body2 size={4} color={Color.White}>
                Please find more information on our{' '}
                <LandingPageButton
                  startOnboarding={startOnboarding}
                  asLink
                  sectionName={MealPlanningSections.FAQ_SECTION}
                  target={TargetUri.BOX_PRICES}
                  action={TrackingActions.BOX_PRICING_NAVIGATION_CLICKED}
                  whiteText
                >
                  <Bold>box prices page.</Bold>
                </LandingPageButton>{' '}
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion
            id="2"
            title={renderAccordionHeader('What do you actually get in a Gousto recipe box?')}
          >
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Every box is filled with delicious, fresh ingredients that&apos;ll make every dinner
                sizzle.
                <Space size={4} />
                Here’s what to expect in your Gousto recipe box:
                <ul>
                  <li>
                    Recipe cards with easy-to-follow instructions on how to cook your recipes (you
                    can save these and make the recipe again, share them with friends, or pop them
                    in your paper recycling)
                  </li>
                  <li>
                    Any extras from the Gousto Market you added to your order (such as wine,
                    chocolate, kitchen equipment, etc.)
                  </li>
                  <li>
                    From time to time, we&apos;ll pop in a little treat (we&apos;ll label these so
                    you know it&apos;s not there by mistake)
                  </li>
                  <li>
                    And, most importantly, all the ingredients for your chosen recipes (except a few
                    basics like oil and salt), including chilled produce, fresh vegetables, herbs,
                    spices, and condiments.
                  </li>
                  <li>
                    If it&apos;s your first box, you&apos;ll also get a copy of our Welcome Booklet,
                    full of tips and advice to help you get the most out of your recipe box.
                  </li>
                </ul>
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion
            id="3"
            title={renderAccordionHeader('How many meals do you get in a Gousto box?')}
          >
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                You can choose 2-5 recipes per box. Please see our{' '}
                <LandingPageButton
                  startOnboarding={startOnboarding}
                  asLink
                  sectionName={MealPlanningSections.FAQ_SECTION}
                  target={TargetUri.BOX_PRICES}
                  action={TrackingActions.BOX_PRICING_NAVIGATION_CLICKED}
                  whiteText
                >
                  <Bold>pricing page</Bold>
                </LandingPageButton>{' '}
                for a full breakdown of box options and prices.
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion
            id="4"
            title={renderAccordionHeader('When will my Gousto menu and ingredients be delivered?')}
          >
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                You can choose which day of the week you prefer to have your Gousto menu delivered.
                On your chosen delivery day, you will receive an update from the courier confirming
                your order is out for delivery and the timeslot in which it will be delivered.
                Please be aware that your box may be delivered outside of this timeslot depending on
                the driver&apos;s route.
                <Space size={4} />
                Our delivery windows are between 8am-7pm, and we expect all boxes to arrive within
                this time. If your box has still not been delivered by 7pm, please contact our
                Customer Care team, who can look into this for you. You can track your delivery
                through the tracking link sent to you on the day of delivery, or by clicking ‘track
                this box’ on the app or ‘track my box’ on the website.
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion
            id="5"
            title={renderAccordionHeader('How will you get my Gousto meals to me if I’m not home?')}
          >
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                If you won’t be home for delivery, don’t worry, our couriers can still safely
                deliver your box to your address. Please let us know a safe place to leave your box,
                and then collect it when you get home. Just follow these{' '}
                <Link
                  variant={LinkVariant.None}
                  href={TargetUri.SAFE_SPACE_HELP}
                  style={{ color: 'white', textDecorationLine: 'underline' }}
                  onClick={() =>
                    simpleTrackRedirect(
                      TrackingActions.HELP,
                      MealPlanningSections.FAQ_SECTION,
                      TargetUri.SAFE_SPACE_HELP,
                    )
                  }
                >
                  <Bold>simple steps to set your safe place.</Bold>
                </Link>{' '}
              </Body2>
            </Box>
          </Accordion>
        </Box>
        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion id="6" title={renderAccordionHeader('What are the Gousto meals this week?')}>
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Browse all the delicious recipes in this week’s menu and get meal planning today!
              </Body2>
            </Box>
          </Accordion>
        </Box>
        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion id="7" title={renderAccordionHeader('Can I skip my food delivery plan?')}>
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Of course! You can skip a delivery whenever you need to, just make sure to let us
                know before the cut-off point: 12pm (noon), 3 days before your delivery day.
              </Body2>
            </Box>
          </Accordion>
        </Box>
        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion
            id="8"
            title={renderAccordionHeader('Do you get to pick your meals on Gousto?')}
          >
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Yes, on Gousto, you get to choose your own meals from a menu of 175+ recipes each
                week. You can choose from different frequency options (weekly, fortnightly, or
                monthly) and the number of meals per box (2, 3, 4, or 5). You have until 12 pm
                (noon) three days before your delivery day to make your choices and changes.
                <Space size={4} />
                If you forget to choose your recipes, our clever algorithm learns what you like, so
                we&apos;ll put a chef&apos;s selection menu together for you, based on your
                subscription settings as well as your previous recipe selections.
              </Body2>
            </Box>
          </Accordion>
        </Box>
        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion id="9" title={renderAccordionHeader('What meals do you get with Gousto?')}>
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                We&apos;ve thought of everything, so you don&apos;t have to. From crowd-pleasing
                mains (meaty, veggie, fishy—you name it) to slurp-worthy soups, tasty sides and
                desserts that deserve a drumroll, you can build a box that&apos;s basically a round
                of applause in food form. Browse all the delicious recipes in{' '}
                <LandingPageButton
                  startOnboarding={startOnboarding}
                  asLink
                  sectionName={MealPlanningSections.FAQ_SECTION}
                  target={TargetUri.MENU}
                  action={TrackingActions.CLICK_RECIPE_NAVIGATION}
                  whiteText
                >
                  <Bold>this week&apos;s menu</Bold>
                </LandingPageButton>{' '}
                and get cooking the magic.
              </Body2>
            </Box>
          </Accordion>
        </Box>
      </Box>
    </Box>
  </Box>
)
